import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, Subject, Subscription, combineLatest, timer } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { CredentialsService } from '../../../../../../core/misc';
import { ResetPasswordEmailUserInputCheck } from '../../../../../../core/model';
import { UserFacade } from '../../../../../../core/user';
import { LoginFormView } from '../../../model/login-form-view.model';

@Component({
  selector: 'py-forgot-password',
  templateUrl: './forgot-password.component.html',
  standalone: false,
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  resetPasswordEmailUserInputCheck$: Observable<ResetPasswordEmailUserInputCheck>;
  error$: Observable<boolean>;
  success$: Observable<boolean>;
  loading$: Observable<boolean>;
  loginFormView = LoginFormView;

  @Output()
  changeView = new EventEmitter<LoginFormView>();

  private subject: Subject<ResetPasswordEmailUserInputCheck>;
  private subscription = new Subscription();

  constructor(
    private userService: UserFacade,
    private credentialsService: CredentialsService
  ) {}

  ngOnInit(): void {
    this.subject = new Subject<ResetPasswordEmailUserInputCheck>();
    this.resetPasswordEmailUserInputCheck$ = this.subject.asObservable();
    this.error$ = this.userService
      .getRequestForgotPasswordEmailError()
      .pipe(map((forgotPasswordError) => Boolean(forgotPasswordError)));
    this.success$ = this.userService.getRequestForgotPasswordEmailSuccess().pipe(
      map((forgotPasswordSuccess) => Boolean(forgotPasswordSuccess)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.loading$ = this.userService.getRequestForgotPasswordEmailLoading();

    this.success$.pipe(filter(Boolean)).subscribe((_success) => {
      this.userService.resetRequestForgotPasswordEmailProcessingState();
    });

    this.subscription.add(
      combineLatest([this.success$, this.error$])
        .pipe(filter(([success, error]) => Boolean(success) || Boolean(error)))
        .subscribe((_success) => {
          this.changeView.emit(this.loginFormView.EmailSentForgotPassword);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  requestReset({ email, username }): void {
    this.userService.requestForgotPasswordEmail(email, username);
    this.subscription.add(
      timer(3000)
        .pipe(take(1))
        .subscribe(() => this.userService.resetRequestForgotPasswordEmailProcessingState())
    );
  }

  checkEmail(email: string): void {
    this.credentialsService
      .isEmailRegistered(email)
      .pipe(take(1))
      .subscribe((res) => this.subject.next({ ...res }));
  }
}
